import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { CalculationResponse } from '../../../../../../../common/dist/angular/responses/calculation.response';
import {
  CalculationCreationOptions,
} from '../../../../../../../common/src/options/calculations/calculation-creation.options';
import { CalculationRunOptions } from '../../../../../../../common/src/options/calculations/calculation-run.options';
import { ListResultResponse } from '../../../../../../../common/dist/angular/responses/list-result.response';
import {
  CalculationTitleUpdateOptions,
} from '../../../../../../../common/src/options/calculations/calculation-title-update.options';
import { CalculationExportOptions } from '../../../../../../../common/dist/angular/options/calculation-export.options';
import { ExportModel } from '../../../../../../../common/dist/angular/models/export.model';
import { TransformerClassType } from '../../core/interceptors/transformer.http-interceptor';
import { DataMapper } from '@pristine-ts/data-mapping-common';
import {
  CalculationRunnerResultsInterface,
} from '../../../../../../../common/src/interfaces/calculation-runner-results.interface';
import { SearchResult } from '../../../../../../../common/src/models/search-result.model';
import {
  SearchQuery,
} from '../../../../../../../server/dist/angular/server/src/microservice/models/search-query.model';
import { CalculationIndexModel } from '../../../../../../../common/src/index-models/calculation.index-model';
import {
  CalculationGrantAdminViewOptions,
} from '../../../../../../../common/src/options/calculations/calculation-grant-admin-view.options';

@Injectable()
export class CalculationsApiController {
  ù;

  constructor(private readonly httpClient: HttpClient,
              private readonly dataMapper: DataMapper,
              @Inject('environment.api.url') private readonly apiUrl: string) {
  }

  public async create(calculationCreationOptions: CalculationCreationOptions): Promise<CalculationResponse> {
    return this.httpClient.post<CalculationResponse>(`${this.apiUrl}/api/calculations`, calculationCreationOptions, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }

  public async run(id: string, calculationRunOptions?: CalculationRunOptions): Promise<CalculationResponse> {
    return this.httpClient.post<CalculationResponse>(`${this.apiUrl}/api/calculations/${id}/run`, calculationRunOptions, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }

  public async instantRun(calculationRunOptions: CalculationRunOptions): Promise<CalculationRunnerResultsInterface<any>> {
    return this.httpClient.post<CalculationRunnerResultsInterface<any>>(`${this.apiUrl}/api/calculations/instant-run`, calculationRunOptions).toPromise();
  }

  public async get(id: string): Promise<CalculationResponse> {
    return this.httpClient.get<CalculationResponse>(`${this.apiUrl}/api/calculations/${id}`, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }

  public async getMetadata(id: string): Promise<CalculationIndexModel> {
    return this.httpClient.get<CalculationIndexModel>(`${this.apiUrl}/api/calculations/${id}/metadata`, {
      context: new HttpContext().set(TransformerClassType, CalculationIndexModel),
    }).toPromise();
  }

  public async list(): Promise<CalculationResponse[]> {
    return this.httpClient.get<ListResultResponse<CalculationResponse>>(`${this.apiUrl}/api/calculations`)
      .pipe(
        switchMap(async (response) => {
          return await this.dataMapper.autoMap(response.items, CalculationResponse);
        }),
      ).toPromise();
  }

  public async search(searchQuery: SearchQuery): Promise<SearchResult<CalculationIndexModel>> {
    return this.httpClient.get<SearchResult<CalculationIndexModel>>(`${this.apiUrl}/api/calculations/_search?${(new HttpParams({ fromObject: JSON.parse(JSON.stringify(searchQuery.exportQueryParameters())) })).toString()}`)
      .pipe(
        switchMap(async (response: SearchResult<CalculationResponse>) => {
          for (let i = 0; i < response.results.length; i++) {
            response.results[i] = await this.dataMapper.autoMap(response.results[i], CalculationIndexModel);
          }

          return response;
        }),
      ).toPromise();
  }

  public async update(id: string, calculationRunOptions: CalculationRunOptions): Promise<CalculationResponse> {
    return this.httpClient.put<CalculationResponse>(`${this.apiUrl}/api/calculations/${id}`, calculationRunOptions, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }

  public async delete(id: string): Promise<void> {
    return this.httpClient.delete<void>(`${this.apiUrl}/api/calculations/${id}`).toPromise();
  }

  public async updateTitle(id: string, calculationTitleUpdateOptions: CalculationTitleUpdateOptions): Promise<CalculationResponse> {
    return this.httpClient.put<CalculationResponse>(`${this.apiUrl}/api/calculations/${id}/title`, calculationTitleUpdateOptions, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }

  public async export(id: string, calculationExportOptions: CalculationExportOptions): Promise<ExportModel> {
    return this.httpClient.post<ExportModel>(`${this.apiUrl}/api/calculations/${id}/export`, calculationExportOptions).toPromise();
  }

  public async grantDelegatedAccess(id: string, options: CalculationGrantAdminViewOptions): Promise<CalculationResponse> {
    return this.httpClient.put<CalculationResponse>(`${this.apiUrl}/api/calculations/${id}/grant-delegated-access-to-admin`, options, {
      context: new HttpContext().set(TransformerClassType, CalculationResponse),
    }).toPromise();
  }
}
